import { useState, useEffect } from 'react';
import api from '@/api';
import { Bell, Check, Loader2, X } from 'lucide-react';

export default function DigestPreferences({ onClose }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/cron/digest-preferences');
        setPrefs(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/cron/digest-preferences', prefs);
      setPrefs(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-sm p-6 w-96">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    </div>
  );

  const frequencies = [
    { id: 'daily', label: 'Daily', desc: 'Every morning at 9am' },
    { id: 'weekly', label: 'Weekly', desc: 'Every Monday at 9am' },
    { id: 'monthly', label: 'Monthly', desc: 'First of each month' },
    { id: 'off', label: 'Off', desc: 'No digest emails' },
  ];

  const sections = [
    { key: 'include_submittals', label: 'Submittal Status', desc: 'Approval counts, flagged substitutions' },
    { key: 'include_rfis', label: 'Outstanding RFIs', desc: 'Open RFIs awaiting architect response' },
    { key: 'include_documents', label: 'Document Progress', desc: 'Division folder collection progress' },
    { key: 'include_assets', label: 'Asset Count', desc: 'Total extracted equipment count' },
    { key: 'include_activity', label: 'Recent Activity', desc: 'New uploads, extractions, and RFIs this period' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="digest-prefs-modal">
      <div className="bg-card border border-border rounded-sm w-[440px] max-h-[80vh] overflow-y-auto shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-heading uppercase tracking-wider text-foreground">Digest Preferences</h3>
          </div>
          <button onClick={onClose} data-testid="close-prefs-btn"
            className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Frequency */}
          <div>
            <p className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground mb-2">Frequency</p>
            <div className="grid grid-cols-4 gap-2">
              {frequencies.map(f => (
                <button key={f.id}
                  onClick={() => setPrefs(p => ({ ...p, frequency: f.id }))}
                  data-testid={`freq-${f.id}`}
                  className={`p-2.5 rounded-sm border text-center transition-colors ${
                    prefs?.frequency === f.id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/30'
                  }`}>
                  <p className="text-xs font-medium">{f.label}</p>
                  <p className="text-[9px] text-muted-foreground mt-0.5">{f.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Include sections */}
          {prefs?.frequency !== 'off' && (
            <div>
              <p className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground mb-2">Include in Digest</p>
              <div className="space-y-1.5">
                {sections.map(s => (
                  <label key={s.key}
                    className="flex items-center gap-3 px-3 py-2 rounded-sm border border-border hover:border-primary/20 transition-colors cursor-pointer"
                    data-testid={`toggle-${s.key}`}>
                    <input type="checkbox"
                      checked={prefs?.[s.key] !== false}
                      onChange={(e) => setPrefs(p => ({ ...p, [s.key]: e.target.checked }))}
                      className="w-3.5 h-3.5 accent-primary" />
                    <div>
                      <p className="text-xs text-foreground">{s.label}</p>
                      <p className="text-[9px] text-muted-foreground">{s.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Save */}
          <div className="flex items-center gap-3 pt-2">
            <button onClick={save} disabled={saving}
              data-testid="save-prefs-btn"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-primary text-primary-foreground rounded-sm hover:opacity-90 transition-opacity disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
              {saving ? 'Saving...' : saved ? 'Saved' : 'Save Preferences'}
            </button>
            <button onClick={onClose}
              className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
